import Modal from '@app/components/Modal/Modal'
import * as ModalStyles from '@app/components/Modal/styled'
import {
  CopyTeeAllHolesEvaluationProgress,
  CopyTeeAllHolesTaskResult,
  CopyTeeHoleState,
} from '@app/evaluation-core'
import { LockStatus, MIHole, MITee } from '@app/evaluation-core/types'
import React, { useMemo } from 'react'
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaLock,
  FaRegClock,
  FaSyncAlt,
} from 'react-icons/fa'

import * as S from './CopyTeeProgressModal.styles'

type Props = {
  isOpen: boolean
  isRunning: boolean
  onClose: () => void
  sourceTee: MITee | null
  targetTee: MITee | null
  holes: MIHole[]
  holeStatuses: Record<string, CopyTeeHoleState>
  progress: CopyTeeAllHolesEvaluationProgress | null
  result: CopyTeeAllHolesTaskResult | null
  errorMessage: string | null
}

const getStatusMeta = (status: CopyTeeHoleState) => {
  switch (status) {
    case 'STARTED':
      return {
        label: 'Copying',
        tone: 'active' as const,
        icon: <FaSyncAlt size={11} />,
        spin: true,
      }
    case 'FINISHED':
      return {
        label: 'Copied',
        tone: 'success' as const,
        icon: <FaCheckCircle size={11} />,
        spin: false,
      }
    case 'ERROR':
      return {
        label: 'Failed',
        tone: 'danger' as const,
        icon: <FaExclamationCircle size={11} />,
        spin: false,
      }
    case 'SKIPPED_LOCKED':
      return {
        label: 'Locked',
        tone: 'locked' as const,
        icon: <FaLock size={11} />,
        spin: false,
      }
    case 'NOT_STARTED':
    default:
      return {
        label: 'Queued',
        tone: 'neutral' as const,
        icon: <FaRegClock size={11} />,
        spin: false,
      }
  }
}

const CopyTeeProgressModal = ({
  isOpen,
  isRunning,
  onClose,
  sourceTee,
  targetTee,
  holes,
  holeStatuses,
  progress,
  result,
  errorMessage,
}: Props) => {
  const holeRows = useMemo(() => {
    return holes.map((hole, index) => {
      const fallbackStatus =
        hole?.locked === LockStatus.LOCKED ? 'SKIPPED_LOCKED' : 'NOT_STARTED'

      return {
        key: String(index),
        holeNumber:
          typeof hole?.holeIndex === 'number' ? hole.holeIndex + 1 : index + 1,
        status: holeStatuses[String(index)] || fallbackStatus,
      }
    })
  }, [holes, holeStatuses])

  const statusCounts = useMemo(() => {
    return holeRows.reduce(
      (acc, row) => {
        acc[row.status] += 1
        return acc
      },
      {
        NOT_STARTED: 0,
        STARTED: 0,
        FINISHED: 0,
        ERROR: 0,
        SKIPPED_LOCKED: 0,
      } as Record<CopyTeeHoleState, number>
    )
  }, [holeRows])

  const processed =
    progress?.progress?.processed ?? result?.processed ?? statusCounts.FINISHED
  const totalUnlocked =
    progress?.progress?.total ??
    result?.totalUnlocked ??
    holeRows.length - statusCounts.SKIPPED_LOCKED

  const safeTotalUnlocked = Math.max(totalUnlocked, 0)
  const safeProcessed = Math.max(processed, 0)
  const remaining = Math.max(safeTotalUnlocked - safeProcessed, 0)
  const completionPct =
    safeTotalUnlocked > 0
      ? Math.min(100, Math.round((safeProcessed / safeTotalUnlocked) * 100))
      : 0

  const currentHoleNumber =
    progress?.progress?.currentHole != null
      ? progress.progress.currentHole + 1
      : null

  const failedCount = result?.failedHoles.length ?? statusCounts.ERROR
  const skippedCount = result?.skippedLocked.length ?? statusCounts.SKIPPED_LOCKED
  const operationState = useMemo(() => {
    if (errorMessage) {
      return {
        label: 'Copy failed. Review details below.',
        tone: 'danger' as const,
        icon: <FaExclamationCircle size={11} />,
        spin: false,
      }
    }

    if (isRunning && currentHoleNumber != null) {
      return {
        label: `Copying hole ${currentHoleNumber}`,
        tone: 'active' as const,
        icon: <FaSyncAlt size={11} />,
        spin: true,
      }
    }

    if (isRunning) {
      return {
        label: 'Finalizing copy operation...',
        tone: 'neutral' as const,
        icon: <FaRegClock size={11} />,
        spin: false,
      }
    }

    if (failedCount > 0) {
      return {
        label: 'Completed with failed holes.',
        tone: 'danger' as const,
        icon: <FaExclamationCircle size={11} />,
        spin: false,
      }
    }

    return {
      label: 'All unlocked holes copied.',
      tone: 'success' as const,
      icon: <FaCheckCircle size={11} />,
      spin: false,
    }
  }, [currentHoleNumber, errorMessage, failedCount, isRunning])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="760px"
      height="78vh"
      closeOnBackdropClick={!isRunning}
      showCloseButton={!isRunning}
    >
      <S.Header>
        <S.Title>
          {isRunning
            ? 'Copying tee adjustments'
            : errorMessage
            ? 'Copy operation failed'
            : 'Copy operation completed'}
        </S.Title>
        <S.Subtitle>
          Live progress across all holes. Locked holes are shown and skipped.
        </S.Subtitle>
        <S.TeeRoute>
          <S.TeePill>
            From: Tee {sourceTee?.teeNumber ?? '-'} ({sourceTee?.name ?? '-'})
          </S.TeePill>
          <S.RouteArrow>to</S.RouteArrow>
          <S.TeePill>
            To: Tee {targetTee?.teeNumber ?? '-'} ({targetTee?.name ?? '-'})
          </S.TeePill>
        </S.TeeRoute>
      </S.Header>

      <ModalStyles.ModalScrollContent>
        <S.Body>
          <S.SummaryGrid>
            <S.SummaryCard>
              <S.SummaryLabel>Processed</S.SummaryLabel>
              <S.SummaryValue>{safeProcessed}</S.SummaryValue>
            </S.SummaryCard>
            <S.SummaryCard>
              <S.SummaryLabel>Remaining</S.SummaryLabel>
              <S.SummaryValue>{remaining}</S.SummaryValue>
            </S.SummaryCard>
            <S.SummaryCard>
              <S.SummaryLabel>Failed</S.SummaryLabel>
              <S.SummaryValue>{failedCount}</S.SummaryValue>
            </S.SummaryCard>
            <S.SummaryCard>
              <S.SummaryLabel>Skipped Locked</S.SummaryLabel>
              <S.SummaryValue>{skippedCount}</S.SummaryValue>
            </S.SummaryCard>
          </S.SummaryGrid>

          <S.ProgressMetaRow>
            <S.ProgressMeta>
              {safeProcessed}/{safeTotalUnlocked} unlocked holes copied
            </S.ProgressMeta>
            <S.ProgressMeta>{completionPct}%</S.ProgressMeta>
          </S.ProgressMetaRow>
          <S.ProgressTrack>
            <S.ProgressFill $width={completionPct} />
          </S.ProgressTrack>

          <S.CurrentStateRow>
            <S.StatusPill $tone={operationState.tone}>
              <S.StatusIcon $spin={operationState.spin}>
                {operationState.icon}
              </S.StatusIcon>
              {operationState.label}
            </S.StatusPill>
          </S.CurrentStateRow>

          {errorMessage && <S.ErrorBanner>{errorMessage}</S.ErrorBanner>}

          <S.ListTitle>Hole by hole status</S.ListTitle>
          <S.HoleList>
            {holeRows.map((row) => {
              const statusMeta = getStatusMeta(row.status)

              return (
                <S.HoleRow key={row.key}>
                  <div>
                    <S.HoleTitle>Hole {row.holeNumber}</S.HoleTitle>
                    <S.HoleMeta>
                      Tee {targetTee?.teeNumber ?? '-'} ({targetTee?.name ?? '-'})
                    </S.HoleMeta>
                  </div>
                  <S.StatusPill $tone={statusMeta.tone}>
                    <S.StatusIcon $spin={statusMeta.spin}>{statusMeta.icon}</S.StatusIcon>
                    {statusMeta.label}
                  </S.StatusPill>
                </S.HoleRow>
              )
            })}
          </S.HoleList>
        </S.Body>
      </ModalStyles.ModalScrollContent>

      <ModalStyles.ModalFooter>
        <S.FooterMessage>
          {isRunning
            ? 'Copy is in progress. This window can be closed when finished.'
            : 'Copy progress is complete.'}
        </S.FooterMessage>
        <S.FooterAction onClick={onClose} disabled={isRunning}>
          {isRunning ? 'Copying...' : 'Close'}
        </S.FooterAction>
      </ModalStyles.ModalFooter>
    </Modal>
  )
}

export default CopyTeeProgressModal
